import { memo, type MouseEvent, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { LoadType, SingularStationArea } from '@thive/fddm-types';

import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import {
  getStationCarrierSide,
  getStationWidthForCarrierSide,
  type StationCarrierSide,
} from '@/modules/floorplanDesign/helpers/stationCapacity';
import { rotateConstellation } from '@/modules/floorplanDesign/helpers/rotateConstellation';
import {
  getLogicalPoiAngle,
  getStoredPoiAngle,
} from '@/modules/floorplanDesign/helpers/poiAngleHelpers';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { useAppStore } from '@/modules/app/store/useAppStore';
import { getCommonOption } from '@/modules/common/helpers/commonValue';
import {
  createEmptyAreas,
  pushAreaToItsCollection,
} from '@/modules/floorplanDesign/helpers/helpers';

interface Props {
  selectedSingularStationAreas: SingularStationArea[];
}

type StationWithLoadType = {
  area: SingularStationArea;
  loadType: LoadType | undefined;
  carrierSide: StationCarrierSide;
};

/**
 * @description Returns the area turned to the given carrier side, or null when nothing changes.
 * Stations that always fit the load carrier change their width; the others rotate their POI and
 * constellation.
 */
const withCarrierSide = (
  { area, loadType, carrierSide }: StationWithLoadType,
  nextSide: StationCarrierSide,
): SingularStationArea | null => {
  const [loadHandlingPoiSpec] = area.loadHandlingPoiSpecs;
  if (!loadHandlingPoiSpec || carrierSide === nextSide) return null;

  if (loadHandlingPoiSpec.alwaysFitStation) {
    if (!loadType) return null;

    return {
      ...area,
      stationSpec: {
        ...area.stationSpec,
        width: getStationWidthForCarrierSide(loadType, nextSide),
      },
    };
  }

  const constellationAngleDelta = getLogicalPoiAngle(nextSide) - getLogicalPoiAngle(carrierSide);

  return {
    ...area,
    loadHandlingPoiSpecs: [
      {
        ...loadHandlingPoiSpec,
        angle: getStoredPoiAngle(nextSide),
      },
    ],
    constellationSpec: {
      ...area.constellationSpec,
      angle: rotateConstellation(area.constellationSpec.angle, constellationAngleDelta),
    },
  };
};

function CarrierSideComponent({ selectedSingularStationAreas }: Props) {
  const { t } = useTranslation('interface');
  const mode = useAppStore((state) => state.mode);
  const fddmService = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);

  // Only stations with a load handling POI have a carrier side
  const stations = useMemo(
    () =>
      selectedSingularStationAreas.flatMap((area): StationWithLoadType[] => {
        if (!area.loadHandlingPoiSpecs[0]) return [];

        const loadType = fddmService.getLoadType(area.stationSpec.loadTypeIds[0]);
        return [{ area, loadType, carrierSide: getStationCarrierSide(area, loadType) }];
      }),
    [fddmService, selectedSingularStationAreas],
  );

  // null (no button selected) when the stations have different carrier sides
  const selectedCarrierSide = useMemo(
    () => getCommonOption(stations.map(({ carrierSide }) => carrierSide)),
    [stations],
  );

  const handleCarrierSideChange = useCallback(
    (_event: MouseEvent<HTMLElement>, nextSide: StationCarrierSide | null) => {
      if (!nextSide || nextSide === selectedCarrierSide) return;

      const updatedAreas = createEmptyAreas();
      let hasRotatedStation = false;

      stations.forEach((station) => {
        const updatedArea = withCarrierSide(station, nextSide);
        if (!updatedArea) return;

        if (!station.area.loadHandlingPoiSpecs[0]?.alwaysFitStation) hasRotatedStation = true;
        pushAreaToItsCollection(updatedAreas, updatedArea);
      });

      if (hasRotatedStation) {
        fddmService.setPreference(
          'singularStationArea.loadHandlingPoiSpecs.angle',
          getStoredPoiAngle(nextSide),
        );
      }
      fddmService.updateAreas(updatedAreas);
    },
    [fddmService, selectedCarrierSide, stations],
  );

  return (
    <PropertiesLabel i18nkey='interface:properties.area.load_carrier_orientation.label'>
      <ToggleButtonGroup
        value={selectedCarrierSide}
        exclusive
        onChange={handleCarrierSideChange}
        disabled={mode !== 'EDITABLE' || stations.length === 0}
      >
        <ToggleButton value='short' aria-label={t('interface:properties.area.load_carrier_orientation.short')}>
          {t('interface:properties.area.load_carrier_orientation.short')}
        </ToggleButton>

        <ToggleButton value='long' aria-label={t('interface:properties.area.load_carrier_orientation.long')}>
          {t('interface:properties.area.load_carrier_orientation.long')}
        </ToggleButton>
      </ToggleButtonGroup>
    </PropertiesLabel>
  );
}

export const CarrierSide = memo(CarrierSideComponent);
